import {trans} from '@frontend/helpers'
import {useUserData} from '@frontend/hooks'
import {FavouritesApi} from '@frontend/modules/private/favourites/FavouritesApi'
import {useAuthStore} from '@frontend/stores'
import {
    iconHeart,
    iconHeartOutline,
} from '@shared/helpers/icons'
import clsx from 'clsx'
import {
    useEffect,
    useState,
} from 'react'
import {useLocation} from 'react-router-dom'
import {IconButton} from 'swayok-react-mdb-ui-kit/components/Icon/IconButton'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import type {TextColors} from 'swayok-react-mdb-ui-kit/types/Common'
import {AnalyticsEventReporter} from '../../../../../helpers/AnalyticsEventReporter'
import type {AdLikeButtonIconProps} from '../../AdsModuleTypes'

// Кнопка добавления/удаления объявления в избранное.
export function AdLikeIconButton(props: AdLikeButtonIconProps) {

    const {
        ad,
        // Нельзя использовать напрямую, поэтому переименовываем.
        isFavourite: propsValue,
        white,
        noLabel,
        filled,
        disabled,
        className,
        onFavoriteStateChange,
        size = 22,
    } = props

    const location = useLocation()
    const isLoggedIn: boolean = !!useUserData()
    const initUserAuthModal = useAuthStore(
        state => state.initUserAuthModal
    )

    // Хранение состояния без необходимости перезагрузки списка объявлений.
    const [
        isLiked,
        setIsLiked,
    ] = useState<boolean>(propsValue)

    // Состояние отправки запроса в API.
    const [
        isProcessing,
        setIsProcessing,
    ] = useState<boolean>(false)

    // Изменение состояния после обновления данных исполнителя услуг
    // или входа/выхода пользователя.
    useEffect(() => {
        setIsLiked(propsValue && isLoggedIn)
    }, [propsValue, isLoggedIn])

    // Возврат на текущую страницу после успешной регистрации.
    const afterUserRegistration = (): 'stop' => {
        NavigationService.navigateToLocation(location)
        return 'stop'
    }

    // Добавление/удаление из избранного.
    const saveFavoriteState = useEventCallback((
        newValue: boolean
    ) => {
        if (!isLoggedIn) {
            initUserAuthModal(
                () => {
                    // Тайм-аут нужен на обновление состояния.
                    setTimeout(() => {
                        saveFavoriteState(newValue)
                    }, 500)
                },
                afterUserRegistration
            )
            return
        }
        if (isProcessing) {
            return
        }
        setIsProcessing(true)
        setIsLiked(newValue)
        FavouritesApi.changeStateForAd(ad.id, newValue)
            .then(() => {
                onFavoriteStateChange?.(newValue)
                ToastService.success(
                    newValue
                        ? trans().ads.messages.ad_like_added
                        : trans().ads.messages.ad_like_removed
                )
                if (newValue) {
                    AnalyticsEventReporter.addAdToFavorites(ad)
                }
            })
            .catch((error: ApiError) => {
                setIsLiked(!newValue)
                handleErrorResponse(error)
            })
            .finally(() => {
                setIsProcessing(false)
            })
    })

    let label: string | undefined
    if (!noLabel) {
        label = isLiked
            ? trans().ads.remove_like
            : trans().ads.add_like
    }

    let iconColor: TextColors = 'dark'
    let buttonClass: string = ''
    if (isProcessing) {
        buttonClass = 'disabled'
    } else if (isLiked) {
        buttonClass = 'favorite'
    } else if (white) {
        iconColor = 'white'
    }

    let tooltip: string | undefined
    if (noLabel && !isProcessing) {
        tooltip = isLiked
            ? trans().ads.remove_like
            : trans().ads.add_like
    }

    return (
        <IconButton
            path={isLiked || filled ? iconHeart : iconHeartOutline}
            color={iconColor}
            className={clsx('btn-like flex-shrink-0', className, buttonClass)}
            label={label}
            size={size}
            tooltip={tooltip}
            disabled={isProcessing || disabled}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                saveFavoriteState(!isLiked)
            }}
        />
    )
}
